import { useState } from "react";
import { useLlmProviders, type LlmProviderSafe } from "@/hooks/useLlmProviders";
import { LlmProviderDialog } from "@/components/superadmin/LlmProviderDialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const HEALTH_STYLE: Record<string, string> = {
  healthy: "text-success",
  unhealthy: "text-destructive",
  unknown: "text-muted-foreground",
};

export default function SuperadminLLM() {
  const { providers, loading, error, testProvider, setActive, deleteProvider, refresh } = useLlmProviders();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LlmProviderSafe | undefined>();

  const openAdd = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit = (p: LlmProviderSafe) => { setEditing(p); setDialogOpen(true); };

  const runTest = async (p: LlmProviderSafe) => {
    const r = await testProvider({ id: p.id, baseUrl: p.base_url, defaultModel: p.default_model });
    toast({
      title: r.ok ? "Provider healthy" : "Provider test failed",
      description: r.ok ? `HTTP ${r.httpStatus} · ${r.latencyMs} ms` : (r.error ?? `HTTP ${r.httpStatus}`),
      variant: r.ok ? undefined : "destructive",
    });
    await refresh();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">LLM Providers</h1>
          <p className="text-sm text-muted-foreground">
            Platform-wide AI provider. AI features fall back to build-time env vars when no provider is active.
            Changes take effect within ~1 minute.
          </p>
        </div>
        <Button onClick={openAdd}>Add provider</Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && providers.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No providers configured. AI currently uses the <code>LLM_BASE_URL</code> / <code>LLM_MODEL</code> env vars.
          Add a provider to manage the key here instead.
        </div>
      )}

      {providers.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-start text-muted-foreground">
            <tr>
              <th className="py-2">Label</th><th>Preset</th><th>Model</th><th>Key</th>
              <th>Health</th><th>Active</th><th></th>
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="py-2">{p.label}</td>
                <td>{p.preset}</td>
                <td className="font-mono text-xs">{p.default_model}</td>
                <td className="font-mono text-xs">{p.api_key_last4 ? `•••• ${p.api_key_last4}` : "—"}</td>
                <td className={HEALTH_STYLE[p.health_status]} title={p.health_error ?? ""}>
                  {p.health_status}
                </td>
                <td>
                  {p.is_active ? (
                    <span className="text-success">active</span>
                  ) : (
                    <button className="text-primary underline"
                      onClick={() => setActive(p.id).catch((e) => toast({ title: "Could not activate", description: String(e), variant: "destructive" }))}>
                      set active
                    </button>
                  )}
                </td>
                <td className="space-x-3 text-end">
                  <button className="text-primary underline" onClick={() => runTest(p)}>test</button>
                  <button className="text-primary underline" onClick={() => openEdit(p)}>edit</button>
                  <button className="text-destructive underline"
                    onClick={() => deleteProvider(p.id).catch((e) => toast({ title: "Could not delete", description: String(e), variant: "destructive" }))}>
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <LlmProviderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        provider={editing}
        onSaved={() => { setDialogOpen(false); void refresh(); }}
      />
    </div>
  );
}
